'use client'

import Script from 'next/script'
import { useEffect } from 'react'

import { Container } from '@src/components/Container/Container'
import { OutageSplash } from '@src/components/OutageSplash/OutageSplash'
import { configPublic } from '@src/config.public'
import { useSession } from '@src/hooks/useSession'
import { useLocale } from '@src/i18n/LocaleProvider'
import { ServerActions } from '@src/services/server-actions.service'
import useGeolocationStore from '@src/stores/geolocationStore'
import useAddressSearch from '@src/utils/address-search.util'
import { trackUtagEvent } from '@src/utils/analytics.util'

import { searchAddressFactory } from './SSE.searchAddress'

declare global {
  interface Window {
    initSSE: (page: string) => void
  }
}
interface SSEProps {
  page: 'orders' | 'subscriptions'
  loadStyles?: boolean
  shouldSplash?: boolean
}

export function SSE(props: SSEProps) {
  const { user, authSession, setAuthSession } = useSession()
  const { locale } = useLocale()
  const addressHelpers = useAddressSearch()
  const geolocation = useGeolocationStore((state) => state.geoLocation)

  const initializeSSE = (page: string) => {
    if (typeof window.initSSE !== 'undefined') {
      window.initSSE(page)
    }
  }

  useEffect(() => {
    window.sseData = {
      guid: user?.guid,
      locale: locale.codeSnakeCase,
      userLocale: user?.userLocale,
      entitlements: user?.entitlements,
      customerRoles: user?.roles,
      hasBodiAccess: user?.hasBodiAccess,
      enablePaypal: true,
      enableAwardsPicker: false,
      enableBodTspUpgrade: true,
      session: authSession,
      searchAddress: searchAddressFactory({ geolocation, addressHelpers }),
      utag: {
        link: trackUtagEvent,
        view: trackUtagEvent,
      },
      async updateSession() {
        const session = await ServerActions.parallel.getAuthSession()
        setAuthSession(session)
        this.session = session
        return session
      },
    } // initialize global dependency data
  }, [addressHelpers, authSession, geolocation, locale, setAuthSession, user])

  if (props.shouldSplash || !user || !props.page) return <OutageSplash />

  return (
    <>
      <Container innerSpacing="standard">
        {(props.loadStyles ?? true) ? (
          <link rel="stylesheet" href={configPublic.sse.cssUrl} />
        ) : null}
        <div id="root" />
        <Script
          key={props.page}
          src={configPublic.sse.scriptUrl}
          strategy="afterInteractive"
          onReady={() => {
            initializeSSE(props.page)
          }}
          unsafe-inline
        />
      </Container>
    </>
  )
}
